import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader } from './ui/PageHeader';
import apiService from '../services/ApiService';
import Button from './ui/Button';
import type { ProductUpdate } from './home/homeTypes';

export const AdminProductUpdates: React.FC = () => {
  const [items, setItems] = useState<ProductUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [href, setHref] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.adminListProductUpdates();
      setItems(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load updates');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await apiService.adminCreateProductUpdate({
        title: title.trim(),
        body: body.trim() || undefined,
        href: href.trim() || undefined,
        published_at: new Date().toISOString(),
      });
      setTitle('');
      setBody('');
      setHref('');
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    try {
      await apiService.adminDeleteProductUpdate(id);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <PageHeader eyebrow="Admin" title="Product updates" description="Homepage announcements." />
        <p className="text-gray-400 text-sm mt-1">Short announcements shown on the authenticated homepage.</p>
      </div>

      {error ? (
        <div role="alert" className="rounded-lg border border-red-800 bg-red-950/30 p-4 text-red-300 text-sm">
          {error}
        </div>
      ) : null}

      <form onSubmit={create} className="form-section space-y-3">
        <label className="block">
          <span className="sr-only">Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            className="input"
            required
          />
        </label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Body"
          rows={3}
          className="input"
          aria-label="Body"
        />
        <input
          type="text"
          value={href}
          onChange={(e) => setHref(e.target.value)}
          placeholder="Optional link path (e.g. /events)"
          className="input"
          aria-label="Link"
        />
        <Button type="submit" variant="primary" loading={saving}>
          Publish
        </Button>
      </form>

      {loading ? (
        <p className="text-gray-400 text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-gray-500 text-sm">No product updates yet.</p>
      ) : (
        <ul className="divide-y divide-white/[0.06] border border-white/[0.08] rounded-xl overflow-hidden">
          {items.map((u) => (
            <li key={u.id} className="px-4 py-3 flex items-start justify-between gap-3 bg-[#0c0c0c]/50">
              <div className="min-w-0">
                <p className="text-white font-medium text-sm">{u.title}</p>
                {u.body ? <p className="text-gray-400 text-xs mt-1">{u.body}</p> : null}
              </div>
              <Button type="button" variant="danger" size="sm" onClick={() => void remove(u.id)}>
                Delete
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminProductUpdates;
